/**
 * =============================================================================
 * Componente: AffiliateAdminCreateModal
 * Versão: 1.0.0
 *
 * Descrição:
 *   Modal de cadastro administrativo (interno) de afiliados, acessível pela
 *   tela /tenant/indicacao/afiliados via botão "Novo Afiliado".
 *
 *   Diferente do fluxo público (`affiliate-signup`, usado na landing /r/:slug),
 *   este modal NÃO cria conta `auth.user` — registra apenas o afiliado para
 *   moderação/comissionamento manual. Caso o afiliado precise acessar o portal
 *   posteriormente, basta cadastrar-se pela landing pública com o mesmo e-mail
 *   (a unicidade `(tenant_id, email)` é garantida pela RPC).
 *
 * Validação:
 *   - Programa, nome e e-mail são obrigatórios.
 *   - E-mail validado via regex.
 *   - Tipo de documento obrigatório quando documento informado.
 *
 * Release notes:
 *   v1.0.0 - Versão inicial.
 *   v1.1.0 - Altura limitada com rolagem interna no desktop; máscaras
 *            automáticas em telefone e documento (CPF/CNPJ); lowercase
 *            automático no e-mail.
 *   v1.3.0 - Modo "vincular": quando a prop `affiliate` é informada, o modal
 *            reaproveita a MESMA RPC `create_affiliate` para adicionar uma
 *            pessoa JÁ existente a um novo programa (a RPC localiza pelo
 *            e-mail, não duplica cadastro e não consome quota do plano).
 *            Dados pessoais ficam somente leitura e os programas em que o
 *            afiliado já participa são ocultados do seletor.
 * =============================================================================
 */
import { useState, useMemo, useEffect } from 'react';
import { applyPhoneMask, applyCPFMask, applyCNPJMask } from '@/lib/input-masks';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, UserPlus } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from '@/lib/toast';

import { useReferralPrograms } from '@/hooks/useReferralPrograms';
import { useCreateAffiliateAdmin } from '@/hooks/useReferralAffiliates';

// =============================================================================
// Schema de validação (zod)
// =============================================================================
const formSchema = z
  .object({
    programId: z.string().uuid({ message: 'Selecione um programa.' }),
    fullName: z
      .string()
      .trim()
      .min(2, 'Informe o nome completo.')
      .max(200, 'Máximo 200 caracteres.'),
    email: z
      .string()
      .trim()
      .email('E-mail inválido.')
      .max(255, 'Máximo 255 caracteres.'),
    phone: z.string().trim().max(30).optional().or(z.literal('')),
    documentNumber: z.string().trim().max(30).optional().or(z.literal('')),
    documentType: z.enum(['cpf', 'cnpj', 'none']).default('none'),
    notes: z.string().trim().max(2000).optional().or(z.literal('')),
    forceApprove: z.boolean().default(true),
  })
  .refine(
    (v) =>
      !v.documentNumber || v.documentNumber.length === 0 || v.documentType !== 'none',
    {
      path: ['documentType'],
      message: 'Selecione o tipo de documento.',
    },
  );

type FormValues = z.infer<typeof formSchema>;

/**
 * v1.3.0: dados mínimos do afiliado já existente para o modo "vincular".
 * O e-mail é a chave usada pela RPC para reaproveitar a pessoa.
 */
export interface AffiliateAdminLinkTarget {
  id: string;
  full_name: string;
  email: string;
  phone?: string | null;
  document_number?: string | null;
  document_type?: string | null;
  /** Programas em que a pessoa já participa (serão ocultados do seletor). */
  programIds?: string[];
}

interface AffiliateAdminCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * v1.1.0: pré-seleciona o programa e trava o seletor (uso no detalhe do
   * programa, onde o contexto é implícito).
   */
  defaultProgramId?: string;
  /**
   * v1.3.0: quando informado, o modal entra no modo "vincular afiliado
   * existente a um novo programa" (mesma RPC, dados pessoais bloqueados).
   */
  affiliate?: AffiliateAdminLinkTarget | null;
}

// =============================================================================
// Componente
// =============================================================================
export function AffiliateAdminCreateModal({
  open,
  onOpenChange,
  defaultProgramId,
  affiliate = null,
}: AffiliateAdminCreateModalProps) {
  const { data: programs = [], isLoading: loadingPrograms } = useReferralPrograms();
  const createMutation = useCreateAffiliateAdmin();

  // v1.3.0: modo vincular (pessoa já existente)
  const linkMode = !!affiliate;

  // Apenas programas ativos podem receber novos afiliados.
  // No modo vincular, oculta os programas em que a pessoa já tem adesão
  // (evita o erro de duplicidade levantado pela RPC).
  const activePrograms = useMemo(() => {
    const already = new Set(affiliate?.programIds ?? []);
    return programs.filter((p) => p.is_active && !already.has(p.id));
  }, [programs, affiliate]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      programId: defaultProgramId ?? '',
      fullName: '',
      email: '',
      phone: '',
      documentNumber: '',
      documentType: 'none',
      notes: '',
      forceApprove: true,
    },
  });

  // v1.1.0: ao abrir/mudar o programa do contexto, garante que o form
  // reflete o programa pré-selecionado.
  useEffect(() => {
    if (open && defaultProgramId) {
      form.setValue('programId', defaultProgramId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultProgramId]);

  // v1.3.0: no modo vincular, pré-preenche os dados da pessoa existente.
  useEffect(() => {
    if (!open || !affiliate) return;
    const docType =
      affiliate.document_type === 'cpf' || affiliate.document_type === 'cnpj'
        ? affiliate.document_type
        : 'none';
    form.reset({
      programId: defaultProgramId ?? '',
      fullName: affiliate.full_name ?? '',
      email: (affiliate.email ?? '').toLowerCase(),
      phone: affiliate.phone ?? '',
      documentNumber: affiliate.document_number ?? '',
      documentType: docType,
      notes: '',
      forceApprove: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, affiliate?.id]);

  const submitting = createMutation.isPending;

  // Reaplica máscara do documento quando o tipo muda (CPF/CNPJ/none)
  const docType = form.watch('documentType');
  useEffect(() => {
    const current = form.getValues('documentNumber') || '';
    if (!current) return;
    if (docType === 'cpf') form.setValue('documentNumber', applyCPFMask(current));
    else if (docType === 'cnpj') form.setValue('documentNumber', applyCNPJMask(current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType]);

  const handleSubmit = async (values: FormValues) => {
    try {
      const result = await createMutation.mutateAsync({
        programId: values.programId,
        fullName: values.fullName,
        email: values.email,
        phone: values.phone || null,
        documentNumber: values.documentNumber || null,
        documentType:
          values.documentType === 'none'
            ? null
            : (values.documentType as 'cpf' | 'cnpj'),
        notes: values.notes || null,
        forceApprove: values.forceApprove,
      });

      if (linkMode) {
        toast.success(
          result.status === 'active'
            ? 'Afiliado vinculado ao programa com sucesso.'
            : 'Afiliado vinculado. Adesão pendente de aprovação.',
        );
      } else {
        toast.success(
          result.status === 'active'
            ? 'Afiliado cadastrado e ativado com sucesso.'
            : 'Afiliado cadastrado. Pendente de aprovação.',
        );
      }
      form.reset();
      onOpenChange(false);
    } catch (err: unknown) {
      const raw =
        err instanceof Error ? err.message : 'Erro ao cadastrar afiliado.';
      // Corrida entre abas: a adesão pode ter sido criada em paralelo.
      const msg = /duplicate key|already|já participa/i.test(raw)
        ? 'Este afiliado já participa deste programa de indicação.'
        : raw;
      toast.error(msg);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && submitting) return; // Evita fechar enquanto envia
    if (!next) form.reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-screen h-[100dvh] sm:h-[min(90dvh,720px)] sm:w-full sm:max-w-lg p-0 gap-0 sm:rounded-lg flex flex-col"
        preventCloseOnOutsideInteraction={submitting}
      >
        {/* Header */}
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            {linkMode ? 'Adicionar a um programa' : 'Novo Afiliado'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {linkMode
              ? 'Vincula este afiliado já cadastrado a outro programa de indicação — sem duplicar o cadastro.'
              : 'Cadastro interno de afiliado — sem criar conta de acesso ao portal.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col flex-1 min-h-0"
          >
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-4">
                {/* Programa */}
                <FormField
                  control={form.control}
                  name="programId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Programa <span className="text-destructive">*</span>
                      </FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={loadingPrograms || submitting || !!defaultProgramId}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                loadingPrograms
                                  ? 'Carregando...'
                                  : 'Selecione o programa'
                              }
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-h-[280px]">
                          {activePrograms.length === 0 && !loadingPrograms && (
                            <div className="px-2 py-3 text-xs text-muted-foreground">
                              {linkMode
                                ? 'Este afiliado já participa de todos os programas ativos.'
                                : 'Nenhum programa ativo. Crie um em Indicação → Configurações.'}
                            </div>
                          )}
                          {activePrograms.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Nome completo */}
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Nome completo <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ex.: João da Silva"
                          disabled={submitting}
                          /* v1.3.0: no modo vincular os dados da pessoa não podem mudar */
                          readOnly={linkMode}
                          className={linkMode ? 'bg-muted' : undefined}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* E-mail */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        E-mail <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="afiliado@exemplo.com"
                          autoComplete="email"
                          inputMode="email"
                          disabled={submitting}
                          /* v1.3.0: e-mail é a chave usada pela RPC para reaproveitar a pessoa */
                          readOnly={linkMode}
                          className={linkMode ? 'bg-muted' : undefined}
                          {...field}
                          onChange={(e) => field.onChange(e.target.value.toLowerCase())}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Telefone */}
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone</FormLabel>
                      <FormControl>
                        {/* PhoneInput com seletor de DDI — padrão do sistema.
                            Garante envio do telefone em E.164 (+55...) evitando duplicação de contatos. */}
                        <PhoneInput
                          placeholder="(11) 99999-9999"
                          /* v1.3.0: bloqueado no modo vincular */
                          disabled={submitting || linkMode}
                          value={field.value || ''}
                          onChange={(value) => field.onChange(value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Documento + tipo */}
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-3">
                  <FormField
                    control={form.control}
                    name="documentNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Documento</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={
                              docType === 'cnpj'
                                ? '12.345.678/0001-90'
                                : docType === 'cpf'
                                ? '123.456.789-00'
                                : 'Selecione o tipo ao lado'
                            }
                            inputMode="numeric"
                            disabled={submitting || docType === 'none' || linkMode}
                            {...field}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (docType === 'cpf') field.onChange(applyCPFMask(v));
                              else if (docType === 'cnpj') field.onChange(applyCNPJMask(v));
                              else field.onChange(v);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="documentType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={submitting || linkMode}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            <SelectItem value="cpf">CPF</SelectItem>
                            <SelectItem value="cnpj">CNPJ</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Observações internas — apenas no cadastro novo (v1.3.0) */}
                {!linkMode && (
                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Observações internas</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Visíveis apenas para administradores."
                            rows={3}
                            disabled={submitting}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Switch: aprovar imediatamente */}
                <FormField
                  control={form.control}
                  name="forceApprove"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-md border p-3">
                      <div className="space-y-0.5 pr-3">
                        <Label className="text-sm">Aprovar imediatamente</Label>
                        <p className="text-xs text-muted-foreground">
                          {linkMode ? (
                            <>
                              Cria a adesão já com status <strong>ativa</strong>.
                              Desligue para deixar pendente de moderação.
                            </>
                          ) : (
                            <>
                              Cria o afiliado já com status <strong>ativo</strong>.
                              Desligue para deixar pendente de moderação.
                            </>
                          )}
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={submitting}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </ScrollArea>

            {/* Footer */}
            <DialogFooter className="p-4 border-t gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {linkMode
                  ? submitting
                    ? 'Vinculando...'
                    : 'Vincular ao programa'
                  : submitting
                  ? 'Cadastrando...'
                  : 'Cadastrar afiliado'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
